import { useState } from "react";
import { ArrowSquareOut, Key, CheckCircle, Info, CaretRight } from "@phosphor-icons/react";
import { API_REFERENCE, SECTIONS } from "./api_reference_data";

function ApiCard({ api }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 overflow-hidden"
         data-testid={`api-ref-${api.id}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-800/40 transition-colors text-left">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${api.requires_key ? "bg-amber-600/20 text-amber-400" : "bg-emerald-600/20 text-emerald-400"}`}>
          {api.requires_key ? <Key size={14} weight="bold" /> : <CheckCircle size={14} weight="bold" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-semibold text-white truncate">{api.name}</p>
          <p className="text-xs text-stone-400 truncate">{api.purpose}</p>
        </div>
        <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${api.requires_key ? "bg-amber-600/10 text-amber-400" : "bg-emerald-600/10 text-emerald-400"}`}>
          {api.requires_key ? "key required" : "free, no key"}
        </span>
        <CaretRight size={14} className={`text-stone-500 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-stone-800/60 text-xs">
          <KV k="Purpose" v={api.purpose} />
          <KV k="Used in" v={Array.isArray(api.used_in) ? api.used_in.join(" · ") : api.used_in} mono />
          <KV k="Test endpoint" v={api.test_endpoint} mono />
          <KV k="Live endpoint" v={api.live_endpoint} mono />
          <KV k="Free tier" v={api.free_tier} />
          {api.signup && api.signup !== "n/a" && api.signup !== "n/a." && (
            <KV k="Sign up" v={
              <a href={api.signup.split(" ")[0]} target="_blank" rel="noreferrer"
                 className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                {api.signup} <ArrowSquareOut size={11} />
              </a>
            } />
          )}
          <KV k="How to insert" v={api.how_to_insert} highlight />
          <KV k="Storage" v={api.storage} mono />
        </div>
      )}
    </div>
  );
}

function KV({ k, v, mono = false, highlight = false }) {
  return (
    <div className="flex flex-col sm:flex-row gap-1 sm:gap-3">
      <span className="text-[10px] text-stone-500 uppercase tracking-wider min-w-[100px] sm:pt-0.5">{k}</span>
      <div className={`flex-1 ${mono ? "font-mono text-stone-400" : "text-stone-300"} ${highlight ? "bg-emerald-600/5 border-l-2 border-emerald-600/30 pl-2 -ml-2" : ""} break-words`}>
        {v}
      </div>
    </div>
  );
}

export default function ApiReference() {
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all"
    ? API_REFERENCE
    : filter === "key"
      ? API_REFERENCE.filter((a) => a.requires_key)
      : API_REFERENCE.filter((a) => !a.requires_key);

  const grouped = SECTIONS.map((section) => ({
    section,
    items: filtered.filter((a) => a.section === section),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-5" data-testid="api-reference">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0">
          <Info size={18} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-white">API Reference</h2>
          <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">
            Every external API the Conexus Foundation platform integrates with — what it&apos;s for, the live and test
            endpoints, whether a key is required, where to sign up, and exactly how to insert the key into the system.
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-stone-800 overflow-x-auto scrollbar-thin">
        {[
          { id: "all",  label: `All (${API_REFERENCE.length})` },
          { id: "key",  label: `Key required (${API_REFERENCE.filter((a) => a.requires_key).length})` },
          { id: "free", label: `No key (${API_REFERENCE.filter((a) => !a.requires_key).length})` },
        ].map((t) => (
          <button key={t.id} onClick={() => setFilter(t.id)}
            data-testid={`api-ref-filter-${t.id}`}
            className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              filter === t.id ? "border-emerald-400 text-emerald-300" : "border-transparent text-stone-500 hover:text-stone-300"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-emerald-600/5 border border-emerald-600/20 rounded-xl p-4 text-xs text-stone-300 leading-relaxed">
        <p className="font-heading font-semibold text-emerald-400 mb-2">Quick insertion guide</p>
        <ol className="space-y-1.5 list-decimal list-inside text-stone-400">
          <li>
            <span className="text-stone-200">NASA / Earthdata / OpenWeather / Resend / SendGrid / Mapbox:</span>{" "}
            insert via <span className="font-mono text-emerald-400">Admin → API Manager</span>.
          </li>
          <li>
            <span className="text-stone-200">Flutterwave (test & live):</span>{" "}
            insert via <span className="font-mono text-emerald-400">Admin → Payments</span> (toggle Mode = Test or Live and paste keys).
          </li>
          <li>
            <span className="text-stone-200">SMTP email:</span>{" "}
            insert via <span className="font-mono text-emerald-400">Admin → Payments → SMTP Email Service</span>.
          </li>
          <li>
            <span className="text-stone-200">Free public APIs</span> (Open-Meteo, USGS, OpenAQ, FIRMS, GIBS, FX, Nominatim, ISS):
            nothing to insert — they work out of the box.
          </li>
        </ol>
      </div>

      {grouped.map((g) => (
        <div key={g.section} className="space-y-2">
          <h3 className="text-[10px] text-stone-500 uppercase tracking-wider font-body pl-1">{g.section}</h3>
          <div className="space-y-2">
            {g.items.map((api) => <ApiCard key={api.id} api={api} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
